import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useScrollToTop } from '../../hooks/useScrollToTop';

const NavigationBar: React.FC = () => {
  const location = useLocation();
  const scrollToTop = useScrollToTop();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => {
    setMobileMenuOpen(false);
    scrollToTop();
  };

  return (
    <>
      {/* Floating Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 mt-4 transition-all duration-300">
        <div
          className={`max-w-6xl mx-auto rounded-full px-6 py-3 flex items-center justify-between transition-all duration-300 ${scrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-md border border-slate-200/80'
            : 'bg-white/70 backdrop-blur-md shadow-sm border border-slate-200/50'
            }`}
        >
          {/* Brand Logo */}
          <Link to="/" onClick={handleNavClick} className="flex items-center space-x-3 group relative cursor-pointer" style={{ textDecoration: 'none' }}>
            <img
              src="/assets/skytion/brand/skytion logo png.png"
              className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              alt="Skytion Enterprises"
            />
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/brands"
              onClick={handleNavClick}
              className={`font-medium tracking-tight transition-colors ${location.pathname.includes('/brands') ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              style={{ textDecoration: 'none' }}
            >
              Brands
            </Link>

            {/* Products Mega Menu Wrapper */}
            <div className="relative group">
              <Link
                to="/products"
                className={`font-medium tracking-tight flex items-center gap-1 py-4 transition-colors ${location.pathname.includes('/products') ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
                style={{ textDecoration: 'none' }}
              >
                Products
                <svg className="w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path></svg>
              </Link>

              {/* Mega Menu Dropdown */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[650px] bg-white/95 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-slate-200/60 opacity-0 invisible translate-y-3 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out z-50 pointer-events-none group-hover:pointer-events-auto">
                <div className="grid grid-cols-3 gap-8">
                  {/* Category 1: Eco-Packaging */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400">Eco-Packaging</h4>
                    <ul className="space-y-3 m-0 p-0 list-none">
                      <li><Link to="/products" onClick={handleNavClick} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>Kraft Paper Cups</Link></li>
                      <li><Link to="/products" onClick={handleNavClick} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>Ripple Paper Cups</Link></li>
                      <li><Link to="/products" onClick={handleNavClick} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>Double Wall Cups</Link></li>
                      <li><Link to="/products" onClick={handleNavClick} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>Single Wall Cups</Link></li>
                    </ul>
                  </div>

                  {/* Category 2: Food Service */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400">Food Service</h4>
                    <ul className="space-y-3 m-0 p-0 list-none">
                      <li><Link to="/products" onClick={handleNavClick} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>Paper Food Containers</Link></li>
                      <li><Link to="/products" onClick={handleNavClick} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>Kraft Bowls</Link></li>
                      <li><Link to="/products" onClick={handleNavClick} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>Wooden Cutlery</Link></li>
                    </ul>
                  </div>

                  {/* Category 3: Signature Tissue */}
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 flex flex-col justify-between">
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Premium Tissue</h4>
                      <ul className="space-y-2 m-0 p-0 list-none mb-4">
                        <li><Link to="/products" onClick={handleNavClick} className="text-sm text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>Dining & Pocket Tissue</Link></li>
                        <li><Link to="/products" onClick={handleNavClick} className="text-sm text-slate-600 hover:text-slate-900 transition-colors" style={{ textDecoration: 'none' }}>HRT & Toilet Rolls</Link></li>
                      </ul>
                    </div>
                    <Link to="/products" onClick={handleNavClick} className="text-xs font-bold text-slate-900 underline underline-offset-4 hover:opacity-70 transition-opacity" style={{ textDecoration: 'none' }}>
                      View Complete Catalog
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/manufacturing"
              onClick={handleNavClick}
              className={`font-medium tracking-tight transition-colors ${location.pathname === '/manufacturing' ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              style={{ textDecoration: 'none' }}
            >
              Manufacturing
            </Link>

            <Link
              to="/about"
              onClick={handleNavClick}
              className={`font-medium tracking-tight transition-colors ${location.pathname === '/about' ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              style={{ textDecoration: 'none' }}
            >
              About
            </Link>

            <Link
              to="/contact"
              onClick={handleNavClick}
              className={`font-medium tracking-tight transition-colors ${location.pathname === '/contact' ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              style={{ textDecoration: 'none' }}
            >
              Contact
            </Link>
          </div>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="/SKYTION-Catalogue.pdf"
              download
              className="hidden sm:flex bg-slate-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold tracking-tight hover:bg-slate-800 hover:-translate-y-0.5 transition-all duration-300 shadow-sm hover:shadow-md items-center gap-2"
              style={{ textDecoration: 'none' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Catalogue
            </a>

            <button
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Navigation"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {mobileMenuOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </>
                ) : (
                  <>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`lg:hidden absolute top-full left-4 right-4 mt-2 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl overflow-hidden border border-slate-200 transition-all duration-300 transform origin-top ${mobileMenuOpen ? 'scale-y-100 opacity-100 visible' : 'scale-y-0 opacity-0 invisible h-0'
            }`}
        >
          <div className="flex flex-col p-4 w-full">
            <Link to="/brands" onClick={handleNavClick} className="py-3 px-4 text-slate-700 font-medium hover:bg-slate-50 rounded-lg transition-colors border-b border-slate-100" style={{ textDecoration: 'none' }}>Brands</Link>
            <Link to="/products" onClick={handleNavClick} className="py-3 px-4 text-slate-700 font-medium hover:bg-slate-50 rounded-lg transition-colors border-b border-slate-100" style={{ textDecoration: 'none' }}>Products</Link>
            <Link to="/manufacturing" onClick={handleNavClick} className="py-3 px-4 text-slate-700 font-medium hover:bg-slate-50 rounded-lg transition-colors border-b border-slate-100" style={{ textDecoration: 'none' }}>Manufacturing</Link>
            <Link to="/about" onClick={handleNavClick} className="py-3 px-4 text-slate-700 font-medium hover:bg-slate-50 rounded-lg transition-colors border-b border-slate-100" style={{ textDecoration: 'none' }}>About</Link>
            <Link to="/contact" onClick={handleNavClick} className="py-3 px-4 text-slate-700 font-medium hover:bg-slate-50 rounded-lg transition-colors" style={{ textDecoration: 'none' }}>Contact</Link>

            <a
              href="/SKYTION-Catalogue.pdf"
              download
              className="mt-4 flex bg-slate-900 text-white px-5 py-3 rounded-xl text-sm font-semibold justify-center items-center gap-2 hover:bg-slate-800 transition-colors"
              style={{ textDecoration: 'none' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Download Catalogue
            </a>
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavigationBar;
